import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Route, Routes, useNavigate, useLocation, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { WorkspaceDetailContainer, EmptyStateWrapper, EmptyStateTitle, EmptyStateDescription } from "./WorkspaceDetail.styled";
import { Overview, Users, ActivityLog } from "./pages";
import { Header, Tabs } from "./components";
import { WorkspaceDetailsTabsEnum } from "./enums/workspaceDetailsTabs";
import { useStore } from "../../../../../hooks";
import Icon from "../../../../../elements/icon/Icon";

const WorkspaceDetail = ({ breadcrumbHelpers }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId } = useParams();
  const { workspaceStore } = useStore();
  const [selectedTab, setSelectedTab] = useState(WorkspaceDetailsTabsEnum.OVERVIEW);
  
  // Obtener el workspace actual y su nombre/estado desde el store
  const currentWorkspace = workspaceStore.selectedWorkspace;
  const workspaceName = workspaceStore.getWorkspaceName();
  
  // Determine if workspace is active using isActive field from backend
  const workspaceIsActive = useMemo(() => {
    if (!currentWorkspace) return false;
    
    // Use isActive field directly from backend response
    // Backend returns isActive: true/false
    return currentWorkspace.isActive === true || currentWorkspace.is_active === true;
  }, [currentWorkspace]);

  /**
   * Helper: Get segment after workspaceId from pathname
   * Returns the tab segment (overview, users, activity-log) or null
   */
  const getSegmentAfterWorkspaceId = useCallback((pathname) => {
    const pathParts = pathname.split('/').filter(Boolean);
    const workspaceIndex = pathParts.indexOf('workspace');
    
    if (workspaceIndex === -1) {
      return null;
    }
    
    return pathParts[workspaceIndex + 2] || null;
  }, []);

  /**
   * Helper: Map tab enum to route string
   */
  const getTabRoute = useCallback((tabIndex) => {
    const routeMap = {
      [WorkspaceDetailsTabsEnum.OVERVIEW]: "overview",
      [WorkspaceDetailsTabsEnum.USERS]: "users",
      [WorkspaceDetailsTabsEnum.ACTIVITY_LOG]: "activity-log",
    };
    return routeMap[tabIndex] || "overview";
  }, []);

  /**
   * Helper: Map route string to tab enum
   */
  const getTabFromRoute = useCallback((pathname) => {
    const segment = getSegmentAfterWorkspaceId(pathname);
    
    if (!segment) {
      return WorkspaceDetailsTabsEnum.OVERVIEW;
    }
    
    const tabMap = {
      "overview": WorkspaceDetailsTabsEnum.OVERVIEW,
      "users": WorkspaceDetailsTabsEnum.USERS,
      "activity-log": WorkspaceDetailsTabsEnum.ACTIVITY_LOG,
    };
    
    return tabMap[segment] || WorkspaceDetailsTabsEnum.OVERVIEW;
  }, [getSegmentAfterWorkspaceId]);

  /**
   * Fetch workspace data from API using tenant_uuid from URL
   * 
   * This effect:
   * 1. Extracts tenant_uuid from URL params (workspaceId)
   * 2. Clears previous workspace if URL changed
   * 3. Fetches new workspace data from API using fetchWorkspaceById(tenant_uuid)
   * 4. Falls back to location.state data if API fails
   * 
   * Runs whenever workspaceId (tenant_uuid) changes in the URL
   */
  useEffect(() => {
    if (!workspaceId) {
      return;
    }
    
    // Get selected workspace ID from store (supports both snake_case and camelCase)
    // Calculate directly here to avoid dependency issues
    const selectedWorkspace = workspaceStore.selectedWorkspace;
    const selectedWorkspaceId = selectedWorkspace?.tenant_uuid || 
                                selectedWorkspace?.tenantUuid ||
                                selectedWorkspace?.tenant_id || 
                                selectedWorkspace?.id;
    
    // If the URL workspaceId is different from the selected one, clear the store first
    // This ensures we don't show stale data from a previous workspace
    if (selectedWorkspace && selectedWorkspaceId !== workspaceId) {
      workspaceStore.clearSelectedWorkspace();
    }
    
    // Skip fetch if we already have the correct workspace loaded
    // This prevents unnecessary API calls when navigating between tabs of the same workspace
    if (selectedWorkspace && selectedWorkspaceId === workspaceId) {
      return;
    }
    
    // Fetch workspace from API using the tenant_uuid from URL
    // Or load mock data if workspaceId is "mock-uuid123"
    const fetchWorkspace = async () => {
      // Check if this is a mock workspace
      if (workspaceId === "mock-uuid123") {
        try {
          const mockResponse = await fetch("/mock-overview-workspace.json");
          const mockData = await mockResponse.json();
          
          if (mockData.body) {
            workspaceStore.setSelectedWorkspace(mockData.body);
          }
        } catch (error) {
          console.error("Error loading mock workspace:", error);
        }
        return;
      }
      
      // Regular API call for real workspaces
      try {
        await workspaceStore.fetchWorkspaceById(workspaceId);
      } catch (error) {
        console.error("Error fetching workspace:", error);
        
        // Fallback: Try to use data from location.state if API call fails
        if (location.state?.workspaceData) {
          const stateData = location.state.workspaceData;
          const stateWorkspaceId = stateData.tenant_uuid || stateData.tenant_id;
          
          if (stateWorkspaceId === workspaceId) {
            workspaceStore.setSelectedWorkspace(stateData);
          }
        }
      }
    };
    
    fetchWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  /**
   * Update selected tab from URL and handle redirects
   */
  useEffect(() => {
    const tabFromUrl = getTabFromRoute(location.pathname);
    setSelectedTab(tabFromUrl);
    
    // Redirect to /overview if no tab segment is present
    const segment = getSegmentAfterWorkspaceId(location.pathname);
    if (workspaceId && !segment) {
      navigate(`/v2/workspace/${workspaceId}/overview`, { replace: true });
    }
  }, [location.pathname, navigate, workspaceId, getTabFromRoute, getSegmentAfterWorkspaceId]);

  /**
   * Update breadcrumbs when tab or workspace changes
   */
  const tabLabel = useMemo(() => {
    const tabFromUrl = getTabFromRoute(location.pathname);
    const labelMap = {
      [WorkspaceDetailsTabsEnum.OVERVIEW]: "Overview",
      [WorkspaceDetailsTabsEnum.USERS]: "Users",
      [WorkspaceDetailsTabsEnum.ACTIVITY_LOG]: "Activity Log",
    };
    return labelMap[tabFromUrl] || "Overview";
  }, [location.pathname, getTabFromRoute]);

  useEffect(() => {
    if (!workspaceId) return;
    
    const tabFromUrl = getTabFromRoute(location.pathname);
    const tabRoute = getTabRoute(tabFromUrl);
    const tabHref = `/v2/workspace/${workspaceId}/${tabRoute}`;
    
    breadcrumbHelpers?.setPageBreadcrumb?.(null, null, {
      tabLabel: tabLabel,
      tabHref: tabHref,
    });
  }, [breadcrumbHelpers, location.pathname, workspaceId, tabLabel, getTabRoute, getTabFromRoute]);

  /**
   * Handle tab change and update URL
   */
  const handleTabChange = useCallback((index) => {
    setSelectedTab(index);
    const newRoute = getTabRoute(index);
    const basePath = `/v2/workspace/${workspaceId}`;
    
    navigate(`${basePath}/${newRoute}`, { replace: true });
  }, [workspaceId, navigate, getTabRoute]);

  // Determine if we should show skeletons (loading but no workspace data yet)
  const isLoading = workspaceStore.isLoading && !workspaceStore.selectedWorkspace;

  // Show empty state if workspace fetch failed (workspace not found)
  if (workspaceStore.error && !workspaceStore.selectedWorkspace) {
    return (
      <WorkspaceDetailContainer>
        <EmptyStateWrapper>
          <Icon name="noDataAvailable" width="201px" height="195px" />
          <EmptyStateTitle>Workspace not found</EmptyStateTitle>
          <EmptyStateDescription>
            This workspace does not exist.
          </EmptyStateDescription>
        </EmptyStateWrapper>
      </WorkspaceDetailContainer>
    );
  }

  return (
    <WorkspaceDetailContainer>
      <Header 
        title={workspaceName} 
        isActive={workspaceIsActive}
        workspaceId={workspaceId}
        isLoading={isLoading}
      />
      <Tabs key={selectedTab} onTabChange={handleTabChange} defaultIndex={selectedTab} />
      <Routes>
        <Route path="overview" element={<Overview isLoading={isLoading} />} />
        <Route path="users" element={<Users workspaceId={workspaceId} />} />
        <Route path="activity-log" element={<ActivityLog workspaceId={workspaceId} />} />
      </Routes>
    </WorkspaceDetailContainer>
  );
};

export default observer(WorkspaceDetail);
